import {
  QueryObserverResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import { getIsSponsoredListingsEligibleEvent } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BoostedSale,
  CampaignManagerClient,
  DateTimeRange,
  Permission,
  SponsoredListingsCampaign,
  Venue,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContextDataSelector } from '../CatalogDataContext';
import { ErrorTypes, useClientLogContext } from '../ClientLogContext';
import { useBoostedEventIdsFromCampaigns } from './useBoostedEventIdsFromCampaigns ';

const BASE_QUERY_KEY = 'AdCampaignsDataContext.getCampaignData';

/**
 * Basic sales aggregate data structure.
 */
export type SalesAggregate = {
  saleCount: number;
  totalCost: number;
  totalNetProceeds: number;
  currencyCode: string;
};

/**
 * Aggregated sales data for a campaign, combining all sales from all flights under the campaign.
 * Contains the total cost, total net proceeds, currency code, and per-flight breakdown.
 */
export type CampaignSalesAggregate = SalesAggregate & {
  /**
   * Aggregated sales data broken down by flight ID under this campaign.
   * Each flight's aggregate includes total cost, total net proceeds, and currency code from all sales in that flight.
   */
  flightsAggregate: Record<number, SalesAggregate>;
};

export type IAdCampaignsDataContext = {
  adCampaignsDataQuery: UseQueryResult<Record<
    number,
    SponsoredListingsCampaign[]
  > | null>;
  hasCampaignsQuery: UseQueryResult<boolean | null>;
  isEventsEnabledForSponsoredListingsLoading: (
    eventId: number | null
  ) => boolean;
  allCampaignData:
    | Record<number, SponsoredListingsCampaign[]>
    | null
    | undefined;
  /**
   * Aggregated sales data by campaign ID. Each campaign's aggregate includes
   * total cost, total net proceeds, and currency code from all sales across all flights.
   * Usage: campaignSalesAggregates[campaignId] to get aggregate for a specific campaign.
   */
  campaignSalesAggregates?: Record<number, CampaignSalesAggregate> | null;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
  refreshData: () => Promise<QueryObserverResult>;
  /**
   * Fetches a single campaign by ID and splices it into the cached
   * Record<eventId, campaigns[]> structure without invalidating the full list.
   * Falls back to a full-list invalidation if the single-fetch fails.
   * No-op when campaignId is null/undefined or <= 0.
   */
  refreshSingleCampaign: (campaignId: number) => Promise<void>;
};

export const AdCampaignsDataContext = createContext<IAdCampaignsDataContext>({
  hasCampaignsQuery: {} as UseQueryResult<boolean | null>,
  adCampaignsDataQuery: {} as UseQueryResult<Record<
    number,
    SponsoredListingsCampaign[]
  > | null>,
  isEventsEnabledForSponsoredListingsLoading: () => false,
  allCampaignData: [],
  campaignSalesAggregates: {},
  refreshData: () => Promise.resolve({} as QueryObserverResult),
  refreshSingleCampaign: async () => {},
});

export const AdCampaignsDataContextProvider = ({
  children,
  loadSalesDetails,
  dateRange,
  fetchAllCampaigns = false,
}: {
  children: React.ReactNode;
  loadSalesDetails?: boolean;
  dateRange?: DateTimeRange | null;
  /**
   * When true, fetches ALL campaigns regardless of the current listings catalog
   * (i.e., passes [] to getCampaigns and uses campaignEventIds for getBoostedSales).
   * When false (default), uses the legacy behavior: scopes campaigns to the
   * listings-derived eventIds from the current catalog context.
   * Only set this to true for the Sponsored Listings page.
   */
  fetchAllCampaigns?: boolean;
}) => {
  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const { appContext } = useAppContext();
  const allowedCountryCodes = appContext?.sponsoredListingsAllowedCountryCodes;

  const data = useCatalogDataContextDataSelector();
  const eventIds = useMemo(() => {
    return Object.values(data?.events || {})
      .filter((ev) => {
        const venue = data?.venues?.[ev.event?.venId ?? ''] || ({} as Venue);
        return (
          ev?.event?.viagId != null &&
          getIsSponsoredListingsEligibleEvent(
            venue.ctryCode,
            allowedCountryCodes
          )
        );
      })
      .map((ev) => Number(ev.event.viagId))
      .filter((id): id is number => id != null)
      .toSorted();
  }, [data?.events, data?.venues, allowedCountryCodes]);

  const {
    hasCampaignsQuery,
    campaignDataQuery,
    isEventsEnabledForSponsoredListingsLoading,
    allCampaignData,
    campaignSalesAggregates,
    refreshData,
    refreshSingleCampaign,
  } = useGetCampaignDataQuery(
    eventIds,
    setErrorInfo,
    loadSalesDetails,
    dateRange,
    fetchAllCampaigns
  );

  useBoostedEventIdsFromCampaigns(allCampaignData);

  if (!hasAdPlatformViewPermission) {
    return <>{children}</>;
  }

  return (
    <AdCampaignsDataContext.Provider
      value={{
        hasCampaignsQuery,
        isEventsEnabledForSponsoredListingsLoading,
        adCampaignsDataQuery: campaignDataQuery,
        allCampaignData,
        campaignSalesAggregates,
        errorInfo,
        refreshData,
        refreshSingleCampaign,
      }}
    >
      {children}
    </AdCampaignsDataContext.Provider>
  );
};

export const useAdCampaignsDataContext = () =>
  useContext(AdCampaignsDataContext);

export const useGetCampaignDataQuery = (
  eventIds: number[],
  setErrorInfo: (
    value: React.SetStateAction<
      | {
          errorHeader: React.ReactNode;
          errorMessage: React.ReactNode;
        }
      | undefined
    >
  ) => void,
  loadSalesDetails?: boolean,
  dateRange?: DateTimeRange | null,
  fetchAllCampaigns?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const hasAdPlatformViewPermission = useUserHasAnyOfPermissions(
    Permission.AdPlatform_CampaignsViewAll
  );

  const campaignDataQueryKey = useMemo(
    () => [
      BASE_QUERY_KEY,
      activeAccountWebClientConfig.activeAccountId,
      loadSalesDetails,
      dateRange?.start,
      dateRange?.end,
      // Include the flag so the two modes don't share a React Query cache entry
      fetchAllCampaigns ?? false,
      // For the legacy mode, include the sorted eventIds so different event
      // sets produce different cache keys
      ...(fetchAllCampaigns ? [] : eventIds),
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      loadSalesDetails,
      dateRange?.start,
      dateRange?.end,
      fetchAllCampaigns,
      eventIds,
    ]
  );

  const shouldQueryHasCampaigns = useMemo(
    () =>
      hasAdPlatformViewPermission &&
      activeAccountWebClientConfig.activeAccountId != null,
    [activeAccountWebClientConfig.activeAccountId, hasAdPlatformViewPermission]
  );

  const shouldQueryCampaignData = useMemo(
    () =>
      hasAdPlatformViewPermission &&
      activeAccountWebClientConfig.activeAccountId != null,
    [activeAccountWebClientConfig.activeAccountId, hasAdPlatformViewPermission]
  );

  const campaignDataQuery = useQuery({
    queryKey: campaignDataQueryKey,
    queryFn: async ({ signal }) => {
      setErrorInfo(undefined);

      if (!shouldQueryCampaignData) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          // When fetchAllCampaigns is true, pass [] to get all campaigns
          // regardless of the current listings catalog.
          // When false (default/legacy), pass the listings-derived eventIds
          // so campaigns are scoped to the current catalog.
          const queryEventIds = fetchAllCampaigns ? [] : eventIds;
          // Forward React Query's AbortSignal so that cancelling the query aborts the
          // HTTP request, which propagates as HttpContext.RequestAborted on the server
          // and cancels the downstream CampaignManager gRPC call.
          const data = await client.getCampaigns(
            queryEventIds,
            loadSalesDetails,
            dateRange?.start ?? undefined,
            dateRange?.end ?? undefined,
            signal
          );
          return data.reduce(
            (acc, campaign) => {
              if (acc[campaign.eventId] != null) {
                acc[campaign.eventId].push(campaign);
              } else {
                acc[campaign.eventId] = [campaign];
              }
              return acc;
            },
            {} as Record<number, SponsoredListingsCampaign[]>
          );
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CampaignManagerClient.getCampaignData', error, {
            eventIds,
          });
        }
      );
    },
    enabled: shouldQueryCampaignData,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
  });

  const campaignEventIds = useMemo(() => {
    if (!campaignDataQuery.data) return [];
    const ids = new Set<number>();
    for (const campaigns of Object.values(campaignDataQuery.data)) {
      for (const c of campaigns) {
        if (c.eventId != null && c.eventId > 0) {
          ids.add(c.eventId);
        }
      }
    }
    return Array.from(ids).sort((a, b) => a - b);
  }, [campaignDataQuery.data]);

  // Legacy mode: use listings-derived eventIds for getBoostedSalesByEventIds.
  // New mode (fetchAllCampaigns): use the event IDs extracted from campaign results.
  const boostedSalesEventIds = fetchAllCampaigns ? campaignEventIds : eventIds;

  const loadSalesDetailsQueryKey = useMemo(
    () => [
      'CampaignManagerClient.getBoostedSalesByEventIds',
      activeAccountWebClientConfig.activeAccountId,
      boostedSalesEventIds,
      loadSalesDetails,
    ],
    [
      activeAccountWebClientConfig.activeAccountId,
      boostedSalesEventIds,
      loadSalesDetails,
    ]
  );

  const shouldQueryLoadSalesDetails = useMemo(
    () =>
      hasAdPlatformViewPermission &&
      loadSalesDetails &&
      Boolean(boostedSalesEventIds.length) &&
      activeAccountWebClientConfig.activeAccountId != null,
    [
      hasAdPlatformViewPermission,
      loadSalesDetails,
      boostedSalesEventIds?.length,
      activeAccountWebClientConfig.activeAccountId,
    ]
  );

  const loadSalesDetailsQuery = useQuery({
    queryKey: loadSalesDetailsQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);

      if (!shouldQueryLoadSalesDetails) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const data =
            await client.getBoostedSalesByEventIds(boostedSalesEventIds);
          const boostedSaleByFlights = data.reduce(
            (acc, sale) => {
              if (acc[sale.adCampaignFlightId] != null) {
                acc[sale.adCampaignFlightId].push(sale);
              } else {
                acc[sale.adCampaignFlightId] = [sale];
              }
              return acc;
            },
            {} as Record<number, BoostedSale[]>
          );

          return boostedSaleByFlights;
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CampaignManagerClient.getBoostedSalesByEventIds', error, {
            eventIds: boostedSalesEventIds,
          });
        }
      );
    },
    enabled: shouldQueryLoadSalesDetails,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
  });

  const hasCampaignsQueryKey = useMemo(
    () => [
      'CampaignManagerClient.hasAnyCampaign',
      activeAccountWebClientConfig.activeAccountId,
    ],
    [activeAccountWebClientConfig.activeAccountId]
  );

  const hasCampaignsQuery = useQuery({
    queryKey: hasCampaignsQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);

      if (!shouldQueryHasCampaigns) {
        return null;
      }

      return tryInvokeApi(
        async () => {
          const client = new CampaignManagerClient(
            activeAccountWebClientConfig
          );
          const data = await client.hasAnyCampaign();
          return data;
        },
        (error) => {
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('CampaignManagerClient.hasAnyCampaign', error);
        }
      );
    },
    enabled: shouldQueryHasCampaigns,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
  });

  const eventsEnabledForSponsoredListingsQueryKey = useMemo(
    () => [
      'CampaignManagerClient.getEventsEnabledForSponsoredListings',
      activeAccountWebClientConfig.activeAccountId,
    ],
    [activeAccountWebClientConfig.activeAccountId]
  );

  const shouldQueryHasEventsEnabledForSponsoredListings = useMemo(
    () => activeAccountWebClientConfig.activeAccountId != null,
    [activeAccountWebClientConfig.activeAccountId]
  );

  const eventsEnabledForSponsoredListingsQuery = useQuery({
    queryKey: eventsEnabledForSponsoredListingsQueryKey,
    queryFn: async () => {
      setErrorInfo(undefined);
      if (!shouldQueryHasEventsEnabledForSponsoredListings) {
        return null;
      }
      return tryInvokeApi(async () => {
        const client = new CampaignManagerClient(activeAccountWebClientConfig);
        const data = await client.getEventsEnabledForSponsoredListings();
        return {
          blackList: data.item1,
          whiteList: data.item2,
        };
      });
    },
    enabled: shouldQueryHasEventsEnabledForSponsoredListings,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    networkMode: 'offlineFirst',
  });

  const isEventsEnabledForSponsoredListingsLoading = (
    eventId: number | null
  ) => {
    if (eventId == null) {
      return false;
    }

    if (eventsEnabledForSponsoredListingsQuery.isLoading) {
      return false;
    }

    const blackList =
      eventsEnabledForSponsoredListingsQuery.data?.blackList || [];
    const whiteList =
      eventsEnabledForSponsoredListingsQuery.data?.whiteList || [];

    // If in black-list, then disabled
    if (blackList.includes(eventId)) {
      return false;
    }

    if (whiteList.length > 0) {
      // If we have any in white-list, only those are enabled
      // the rest are disabled
      if (whiteList.includes(eventId)) {
        return true;
      }

      return false;
    }

    // If we have no white-list, and not in black-list, then enabled
    return true;
  };

  const queryClient = useQueryClient();

  const refreshData = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: [BASE_QUERY_KEY, activeAccountWebClientConfig.activeAccountId],
    });
    queryClient.invalidateQueries({
      queryKey: hasCampaignsQueryKey,
    });
    queryClient.invalidateQueries({
      queryKey: loadSalesDetailsQueryKey,
    });
    await hasCampaignsQuery.refetch();
    await loadSalesDetailsQuery.refetch();
    return await campaignDataQuery.refetch();
  }, [
    activeAccountWebClientConfig.activeAccountId,
    campaignDataQuery,
    hasCampaignsQuery,
    hasCampaignsQueryKey,
    loadSalesDetailsQuery,
    loadSalesDetailsQueryKey,
    queryClient,
  ]);

  const refreshSingleCampaign = useCallback(
    async (campaignId: number) => {
      if (!campaignId || campaignId <= 0) {
        return;
      }

      try {
        const client = new CampaignManagerClient(activeAccountWebClientConfig);
        const updatedCampaign = await client.getCampaign(campaignId);

        // Update ALL campaign data query caches (not just this provider's),
        // because the CampaignModal and InventoryEvent page have separate
        // providers with different query keys. GetCampaign (single) returns
        // complete data (including targetRoas, spendStrategy) while
        // GetCampaigns (bulk) may omit null fields.
        queryClient.setQueriesData<Record<
          number,
          SponsoredListingsCampaign[]
        > | null>({ queryKey: [BASE_QUERY_KEY] }, (currentCache) => {
          if (currentCache == null) return currentCache;

          const newCache: Record<number, SponsoredListingsCampaign[]> = {};
          for (const [eventIdKey, campaigns] of Object.entries(currentCache)) {
            const filtered = campaigns.filter(
              (c) => c.campaignId !== campaignId
            );
            if (filtered.length > 0) {
              newCache[Number(eventIdKey)] = filtered;
            }
          }

          const targetBucket = newCache[updatedCampaign.eventId] ?? [];
          newCache[updatedCampaign.eventId] = [
            ...targetBucket,
            updatedCampaign,
          ];
          return newCache;
        });
      } catch (err) {
        trackError(
          'CampaignManagerClient.refreshSingleCampaign',
          err as ErrorTypes,
          {
            campaignId,
          }
        );
        // Fall back to invalidating the full list so the next consumer
        // triggers a normal refetch.
        queryClient.invalidateQueries({
          queryKey: [
            BASE_QUERY_KEY,
            activeAccountWebClientConfig.activeAccountId,
          ],
        });
      }
    },
    [
      activeAccountWebClientConfig,
      campaignDataQueryKey,
      queryClient,
      trackError,
    ]
  );

  const campaignSalesAggregates = useMemo(() => {
    if (!campaignDataQuery.data || !loadSalesDetailsQuery.data) {
      return null;
    }

    const aggregates: Record<number, CampaignSalesAggregate> = {};

    // Iterate through all campaigns to aggregate sales data
    Object.values(campaignDataQuery.data)
      .flat()
      .forEach((campaign) => {
        if (!campaign.campaignFlightIds) {
          return;
        }

        let campaignSaleCount = 0;
        let campaignTotalCost = 0;
        let campaignTotalNetProceeds = 0;
        const currencyCode = campaign.currencyCode;
        const flightsAggregate: Record<number, SalesAggregate> = {};

        // Aggregate sales from all flights under this campaign
        campaign.campaignFlightIds.forEach((flightId) => {
          const salesForFlight = loadSalesDetailsQuery.data![flightId];

          let flightSaleCount = 0;
          let flightTotalCost = 0;
          let flightTotalNetProceeds = 0;

          if (salesForFlight) {
            salesForFlight.forEach((sale) => {
              flightSaleCount += 1;
              flightTotalCost += sale.totalCost ?? 0;
              flightTotalNetProceeds += sale.totalNetProceeds ?? 0;

              // Also add to campaign totals
              campaignSaleCount += 1;
              campaignTotalCost += sale.totalCost ?? 0;
              campaignTotalNetProceeds += sale.totalNetProceeds ?? 0;
            });
          }

          // Store flight-level aggregate
          flightsAggregate[flightId] = {
            saleCount: flightSaleCount,
            totalCost: flightTotalCost,
            totalNetProceeds: flightTotalNetProceeds,
            currencyCode,
          };
        });

        aggregates[campaign.campaignId] = {
          saleCount: campaignSaleCount,
          totalCost: campaignTotalCost,
          totalNetProceeds: campaignTotalNetProceeds,
          currencyCode,
          flightsAggregate,
        };
      });

    return aggregates;
  }, [campaignDataQuery.data, loadSalesDetailsQuery.data]);

  return {
    campaignDataQuery,
    hasCampaignsQuery,
    loadSalesDetailsQuery,
    isEventsEnabledForSponsoredListingsLoading,
    allCampaignData: campaignDataQuery.data,
    campaignSalesAggregates,
    refreshData: refreshData,
    refreshSingleCampaign,
  };
};
